import type {
  Cadence,
  ReportCustomerDto,
  ReportDetailDto,
  ReportTemplateDto,
} from '@act/shared/data-transfer-objects';
import {
  buildDefaultReportName,
  composeReportingPeriodLabel,
  formatCadenceLabel,
  formatWarehouseMeasurementPeriodLabel,
  normalizeWarehouseMeasurementPeriod,
} from '@act/shared/data-transfer-objects';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useReportMetadataEditHydration } from './useReportMetadataEditHydration.hook';
import { useReportMetadataPeriodLoaders } from './useReportMetadataPeriodLoaders.hook';
import {
  deriveMeasurementPeriodMatchStatus,
  resolveAutoMatchedMeasurementPeriod,
  withStoredMeasurementPeriodOption,
  withStoredReportingMonthOption,
} from '../utils/reportMetadataFormOptions.util';

/** Form mode for report metadata state. */
export type ReportMetadataFormMode = 'create' | 'edit';

interface UseReportMetadataFormOptions {
  mode: ReportMetadataFormMode;
  initialReport?: ReportDetailDto | null;
}

/**
 * Shared state and loaders for report create/edit metadata forms.
 * @param options - Form mode and optional initial report for edit hydration.
 * @returns Form state, derived values, and change handlers.
 */
export const useReportMetadataForm = ({
  mode,
  initialReport,
}: UseReportMetadataFormOptions) => {
  const [customerId, setCustomerId] = useState('');
  const [cadence, setCadence] = useState<Cadence>('adhoc');
  const [measurementPeriod, setMeasurementPeriod] = useState('');
  const [endMonthKey, setEndMonthKey] = useState('');
  const [measurementPeriodManuallyEdited, setMeasurementPeriodManuallyEdited] =
    useState(false);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [hydratedReportId, setHydratedReportId] = useState<string | null>(null);

  const [customers, customersLoading, loadCustomers] = useApiCall(
    reportsRequests.listCustomers,
  );
  const [templates, templatesLoading, loadTemplates] = useApiCall(
    reportsRequests.listTemplates,
  );

  const {
    measurementPeriodOptions,
    reportingMonthOptions,
    measurementPeriodsLoading,
    reportingMonthsLoading,
    periodOptionsLoading,
    loadMeasurementPeriods,
    loadReportingMonths,
  } = useReportMetadataPeriodLoaders({
    customerId,
    cadence,
    mode,
    measurementPeriod,
    endMonthKey,
    setMeasurementPeriod,
    setEndMonthKey,
  });

  const selectedCustomer = useMemo(
    () => (customers ?? []).find(customer => customer.id === customerId),
    [customers, customerId],
  );

  const reportingPeriodLabel = useMemo(() => {
    if (cadence === 'adhoc') {
      if (!measurementPeriod.trim()) {
        return '';
      }
      return formatWarehouseMeasurementPeriodLabel(measurementPeriod);
    }
    if (!endMonthKey.trim()) {
      return '';
    }
    return composeReportingPeriodLabel(cadence, '', endMonthKey, '');
  }, [cadence, endMonthKey, measurementPeriod]);

  const autoMatchedMeasurementPeriod = useMemo(
    () =>
      resolveAutoMatchedMeasurementPeriod(
        cadence,
        endMonthKey,
        measurementPeriodOptions.map(option => option.value),
      ),
    [cadence, endMonthKey, measurementPeriodOptions],
  );

  const autoMatchedMeasurementPeriodLabel = useMemo(() => {
    if (!autoMatchedMeasurementPeriod) {
      return '';
    }
    return formatWarehouseMeasurementPeriodLabel(autoMatchedMeasurementPeriod);
  }, [autoMatchedMeasurementPeriod]);

  const measurementPeriodMatchStatus = useMemo(
    () =>
      deriveMeasurementPeriodMatchStatus({
        cadence,
        endMonthKey,
        measurementPeriod,
        autoMatchedMeasurementPeriod,
      }),
    [
      autoMatchedMeasurementPeriod,
      cadence,
      endMonthKey,
      measurementPeriod,
    ],
  );

  const isDirty = useMemo(() => {
    if (mode !== 'edit' || !initialReport) {
      return true;
    }
    const initialMeasurement = normalizeWarehouseMeasurementPeriod(
      initialReport.measurementPeriod ?? '',
    );
    const currentMeasurement = normalizeWarehouseMeasurementPeriod(
      measurementPeriod,
    );
    const initialReporting = initialReport.reportingPeriod.trim();
    const currentReporting = reportingPeriodLabel.trim();
    if (name.trim() !== initialReport.name.trim()) {
      return true;
    }
    if (cadence === 'adhoc') {
      return currentMeasurement !== initialMeasurement;
    }
    return (
      currentReporting !== initialReporting ||
      currentMeasurement !== initialMeasurement
    );
  }, [
    cadence,
    initialReport,
    measurementPeriod,
    mode,
    name,
    reportingPeriodLabel,
  ]);

  const measurementPeriodOptionsForSelect = useMemo(() => {
    const storedPeriod =
      mode === 'edit' && initialReport?.measurementPeriod
        ? initialReport.measurementPeriod
        : '';
    return withStoredMeasurementPeriodOption(
      measurementPeriodOptions,
      storedPeriod,
    );
  }, [initialReport?.measurementPeriod, measurementPeriodOptions, mode]);

  const reportingMonthOptionsForSelect = useMemo(
    () => withStoredReportingMonthOption(reportingMonthOptions, endMonthKey),
    [endMonthKey, reportingMonthOptions],
  );

  const isMetadataHydrated =
    mode !== 'edit' ||
    (initialReport != null && hydratedReportId === initialReport.id);

  const defaultReportName = useMemo(() => {
    if (!selectedCustomer?.name || !reportingPeriodLabel) {
      return '';
    }
    return buildDefaultReportName({
      customerName: selectedCustomer.name,
      reportingPeriod: reportingPeriodLabel,
    });
  }, [reportingPeriodLabel, selectedCustomer?.name]);

  useEffect(() => {
    loadCustomers({ params: null });
    loadTemplates({ params: {} });
  }, [loadCustomers, loadTemplates]);

  useEffect(() => {
    if (nameTouched || !defaultReportName) {
      return;
    }
    setName(defaultReportName);
  }, [defaultReportName, nameTouched]);

  useEffect(() => {
    if (cadence === 'adhoc' || !endMonthKey || measurementPeriodManuallyEdited) {
      return;
    }
    if (autoMatchedMeasurementPeriod) {
      setMeasurementPeriod(autoMatchedMeasurementPeriod);
      return;
    }
    setMeasurementPeriod('');
  }, [
    autoMatchedMeasurementPeriod,
    cadence,
    endMonthKey,
    measurementPeriodManuallyEdited,
  ]);

  useReportMetadataEditHydration({
    mode,
    initialReport,
    customersLoading,
    customers: customers ?? [],
    reportingMonthOptions,
    hydratedReportId,
    setCustomerId,
    setCadence,
    setMeasurementPeriod,
    setEndMonthKey,
    setMeasurementPeriodManuallyEdited,
    setName,
    setNameTouched,
    setHydratedReportId,
    loadMeasurementPeriods,
    loadReportingMonths,
  });

  const handleCustomerChange = useCallback((nextCustomerId: string) => {
    setCustomerId(nextCustomerId);
    setMeasurementPeriodManuallyEdited(false);
    setNameTouched(false);
  }, []);

  const handleCadenceChange = useCallback((nextCadence: Cadence) => {
    setCadence(nextCadence);
    setMeasurementPeriod('');
    setEndMonthKey('');
    setMeasurementPeriodManuallyEdited(false);
    setNameTouched(false);
  }, []);

  const handleTemplateChange = useCallback(
    (nextTemplateId: string) => {
      setTemplateId(nextTemplateId);
      const template = (templates ?? []).find(item => item.id === nextTemplateId);
      if (template) {
        setCadence(template.cadence);
        setMeasurementPeriod('');
        setEndMonthKey('');
        setMeasurementPeriodManuallyEdited(false);
        setNameTouched(false);
      }
    },
    [templates],
  );

  const handleEndMonthKeyChange = useCallback((value: string) => {
    setMeasurementPeriodManuallyEdited(false);
    setEndMonthKey(value);
  }, []);

  const handleMeasurementPeriodChange = useCallback((value: string) => {
    setMeasurementPeriodManuallyEdited(true);
    setMeasurementPeriod(value);
  }, []);

  const handleNameChange = useCallback((value: string) => {
    setNameTouched(true);
    setName(value);
  }, []);

  const handleNameFocus = useCallback(() => {
    setNameTouched(true);
  }, []);

  const handleResetNameToDefault = useCallback(() => {
    setNameTouched(false);
    setName(defaultReportName);
  }, [defaultReportName]);

  const hasPeriodSelection =
    cadence === 'adhoc'
      ? Boolean(measurementPeriod.trim())
      : Boolean(endMonthKey.trim());

  const canSubmit =
    Boolean(customerId && name.trim() && hasPeriodSelection) &&
    (mode === 'edit' ? isDirty : !periodOptionsLoading);

  return {
    customerId,
    cadence,
    measurementPeriod,
    endMonthKey,
    templateId,
    name,
    nameTouched,
    customers: (customers ?? []) as ReportCustomerDto[],
    templates: (templates ?? []) as ReportTemplateDto[],
    measurementPeriodOptions: measurementPeriodOptionsForSelect,
    reportingMonthOptions: reportingMonthOptionsForSelect,
    reportingPeriodLabel,
    cadenceLabel: formatCadenceLabel(cadence),
    measurementPeriodMatchStatus,
    autoMatchedMeasurementPeriodLabel,
    customersLoading,
    templatesLoading,
    measurementPeriodsLoading,
    reportingMonthsLoading,
    defaultReportName,
    canSubmit,
    isDirty,
    isMetadataHydrated,
    handleCustomerChange,
    handleCadenceChange,
    handleTemplateChange,
    handleEndMonthKeyChange,
    handleMeasurementPeriodChange,
    handleNameChange,
    handleNameFocus,
    handleResetNameToDefault,
  };
};
